import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  Plus,
  Save,
  X,
  ArrowRight,
  FileText,
  CheckCircle,
  ClipboardCheck,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Paper,
} from "@mui/material";
import OrderSummary from "./Components/OrderDetailsComponents/OrderSummary";
import OrderItemsTable from "./Components/OrderDetailsComponents/OrderItemsTable";
import { dummyOrders } from "../../../dummyData";
import { successToast } from "../../../services/toast.service";

// Sample customers data for autofill
const sampleCustomers = [
  {
    id: "cust001",
    firmName: "Dream Collection",
    ownerName: "Chaitanya Goyal",
    phoneNumber: "+91 7898747967",
    gstin: "78115264569",
    address: "31, Dwarka dham colony mangliya",
  },
  {
    id: "cust002",
    firmName: "Fashion Hub",
    ownerName: "Rahul Sharma",
    phoneNumber: "+91 9876543210",
    gstin: "65478932145",
    address: "42, MG Road, Indore",
  },
  {
    id: "cust003",
    firmName: "Style Center",
    ownerName: "Priya Patel",
    phoneNumber: "+91 8765432109",
    gstin: "32145698745",
    address: "15, AB Road, Vijay Nagar, Indore",
  },
  {
    id: "cust003",
    firmName: "Paradise Wear",
    ownerName: "Priya Patel",
    phoneNumber: "+91 8765432109",
    gstin: "32145698745",
    address: "15, AB Road, Vijay Nagar, Indore",
  },
  {
    id: "cust003",
    firmName: "Kishan Sales",
    ownerName: "Priya Patel",
    phoneNumber: "+91 8765432109",
    gstin: "32145698745",
    address: "15, AB Road, Vijay Nagar, Indore",
  },
];

const CreateOrderScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [order, setOrder] = useState({
    _id: `ord${Date.now()}`,
    date: new Date().toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    status: "Draft",
    items: [],
    subtotal: 0,
    discount: 0,
    tax: 0,
    grandTotal: 0,
    redeemedVouchers: [],
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Update order with customer details when selected
  useEffect(() => {
    if (selectedCustomer) {
      setOrder(prev => ({
        ...prev,
        firmName: selectedCustomer.firmName,
        ownerName: selectedCustomer.ownerName,
        phoneNumber: selectedCustomer.phoneNumber,
        gstin: selectedCustomer.gstin,
        address: selectedCustomer.address,
      }));
    }
  }, [selectedCustomer]);

  // Handle order items updated
  const handleOrderItemsUpdated = updatedTotals => {
    if (!order) return;

    try {
      setOrder(prev => ({
        ...prev,
        subtotal: updatedTotals?.subtotal || prev.subtotal || 0,
        discount: updatedTotals?.discount || prev.discount || 0,
        tax: updatedTotals?.tax || prev.tax || 0,
        grandTotal: updatedTotals?.grandTotal || prev.grandTotal || 0,
      }));
      successToast("Order details updated successfully");
    } catch (error) {
      console.error("Error updating order totals:", error);
    }
  };

  // Handle save draft
  const handleSaveDraft = () => {
    setLoading(true);
    // In a real app, this would be an API call to save the draft
    setTimeout(() => {
      setLoading(false);
      successToast("Order saved as draft successfully");
      // Navigate to orders list after successful save
      setTimeout(() => {
        navigate("/orders");
      }, 1500);
    }, 800);
  };

  // Handle generate bill
  const handleGenerateBill = () => {
    // Confirm before proceeding
    setConfirmDialogOpen(true);
  };

  // Handle confirm generate bill
  const handleConfirmGenerateBill = () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    // In a real app, this would be an API call to save the order and proceed to bill generation
    setTimeout(() => {
      setLoading(false);

      // For demo, use the current order ID
      const orderId = order._id;

      // Navigate to bill generation
      navigate(`/orders/${orderId}/generate-bill`);
    }, 800);
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <NavLink
            to="/orders"
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ChevronLeft size={20} className="text-gray-600" />
          </NavLink>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Create New Order
          </h1>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Customer Selection */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-base font-medium text-gray-800 mb-4">
              Select Customer
            </h2>
            <Autocomplete
              id="customer-select"
              options={sampleCustomers}
              getOptionLabel={option =>
                `${option.firmName} - ${option.ownerName}`
              }
              onChange={(event, newValue) => {
                setSelectedCustomer(newValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Search Customer"
                  variant="outlined"
                  placeholder="Start typing to search customers..."
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props} className="py-2 px-3">
                  <div>
                    <div className="font-medium">{option.firmName}</div>
                    <div className="text-sm text-gray-500 flex justify-between">
                      <span>{option.ownerName}</span>
                      <span>{option.phoneNumber}</span>
                    </div>
                  </div>
                </li>
              )}
              PaperComponent={props => (
                <Paper elevation={3} {...props} sx={{ maxHeight: 300 }} />
              )}
            />
          </div>

          {/* Order Information Card - only show if customer is selected */}
          {selectedCustomer && <OrderSummary order={order} />}

          {/* Order Items Table - only show if customer is selected */}
          {selectedCustomer && (
            <OrderItemsTable
              orderId={order._id}
              onOrderItemsUpdated={handleOrderItemsUpdated}
              isCreateOrder={true}
            />
          )}

          {/* Action Buttons - Bottom positioned for better UX */}
          <div className="flex items-center justify-end gap-4 mt-4 pb-8">
            <button
              onClick={() => navigate("/orders")}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <X size={18} />
              <span className="font-medium">Cancel</span>
            </button>

            <button
              onClick={handleSaveDraft}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-700 border border-gray-400 hover:bg-gray-50 rounded-lg transition-colors">
              <Save size={18} />
              <span className="font-medium">Save as Draft</span>
            </button>

            <button
              onClick={handleGenerateBill}
              disabled={!selectedCustomer}
              className={`flex items-center gap-2 px-5 py-2.5 text-white rounded-lg transition-colors ${
                !selectedCustomer
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#5652B0] hover:bg-[#4A47A3]"
              }`}>
              <FileText size={18} />
              <span className="font-medium">Generate Bill</span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Generate Bill */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "0",
          },
        }}>
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1F2937",
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <CheckCircle size={22} className="text-[#5652B0]" />
          Generate Bill
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <p className="text-gray-600 mb-4">
            Are you sure you want to generate a bill for this order? This will
            approve the order and proceed to bill generation.
          </p>
          <div className="bg-[#F9F8FF] p-4 rounded-md border border-[#EFEDFB]">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Customer:</span>
              <span className="font-medium">{selectedCustomer?.firmName}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Items Total:</span>
              <span className="font-medium">
                ₹{order.subtotal.toLocaleString()}
              </span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Discount:</span>
                <span className="font-medium text-green-600">
                  -₹{order.discount.toLocaleString()}
                </span>
              </div>
            )}
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Order Total:</span>
              <span className="font-semibold text-[#5652B0]">
                ₹{order.grandTotal.toLocaleString()}
              </span>
            </div>
          </div>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px 24px",
            justifyContent: "flex-end",
            gap: "12px",
          }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGenerateBill}
            variant="contained"
            sx={{
              bgcolor: "#5652B0",
              "&:hover": { bgcolor: "#4A47A3" },
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 20px",
              boxShadow: "none",
            }}>
            Proceed to Bill
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateOrderScreen;
